import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/data/products";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const colorSlug = (item) => (item.color ? "-" + item.color.toLowerCase().replace(/\s+/g, "-") : "");

export const CartDrawer = () => {
  const { open, items, setOpen, remove, changeQty, subtotal } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [email, setEmail] = useState("");

  const handleCheckout = async () => {
    setCheckingOut(true);
    try {
      const grouped = {};
      for (const item of items) {
        if (!grouped[item.id]) grouped[item.id] = { lookup_key: item.id, quantity: 0, sizes: [] };
        grouped[item.id].quantity += item.qty;
        grouped[item.id].sizes.push(`${item.size}${item.color ? " · " + item.color : ""} x${item.qty}`);
      }
      const payload = {
        items: Object.values(grouped).map((g) => ({
          lookup_key: g.lookup_key,
          quantity: g.quantity,
          sizes: g.sizes.join(", "),
        })),
        origin_url: window.location.origin,
        customer_email: email || null,
      };
      const { data } = await axios.post(`${API}/payments/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-zinc-200 bg-white"
          >
            <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
              <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-zinc-900">
                Your Cart <span className="text-tiger">({items.length})</span>
              </h3>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close-button"
                className="flex h-9 w-9 items-center justify-center border border-zinc-300 text-zinc-900 transition-colors hover:border-tiger hover:text-tiger"
                aria-label="Close cart"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center" data-testid="cart-empty-state">
                  <img src="/assets/logo-black.png" alt="" className="w-20 opacity-30" />
                  <p className="font-body text-sm text-zinc-500">Your cart is empty.</p>
                  <p className="font-body text-xs text-zinc-400">Hover a product and pick a size to quick-add.</p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((item) => (
                    <li key={`${item.id}-${item.size}-${item.color || ""}`} data-testid={`cart-item-${item.id}-${item.size.toLowerCase()}${colorSlug(item)}`} className="flex gap-4 border border-zinc-200 bg-fog p-3">
                      <img src={item.image} alt={item.title} className="h-20 w-16 object-cover" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-heading text-xs font-semibold text-zinc-900">{item.title}</p>
                            <p className="mt-0.5 font-body text-[10px] uppercase tracking-widest text-zinc-500">
                              Size {item.size}
                              {item.color ? ` · ${item.color}` : ""}
                            </p>
                          </div>
                          <button
                            onClick={() => remove(item.id, item.size, item.color)}
                            data-testid={`cart-remove-${item.id}-${item.size.toLowerCase()}${colorSlug(item)}`}
                            className="text-zinc-400 transition-colors hover:text-tiger"
                            aria-label="Remove item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center border border-zinc-300 bg-white">
                            <button
                              onClick={() => changeQty(item.id, item.size, item.color, -1)}
                              data-testid={`cart-qty-minus-${item.id}-${item.size.toLowerCase()}${colorSlug(item)}`}
                              className="flex h-7 w-7 items-center justify-center text-zinc-600 hover:text-tiger"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="w-8 text-center font-heading text-xs font-bold text-zinc-900">{item.qty}</span>
                            <button
                              onClick={() => changeQty(item.id, item.size, item.color, 1)}
                              data-testid={`cart-qty-plus-${item.id}-${item.size.toLowerCase()}${colorSlug(item)}`}
                              className="flex h-7 w-7 items-center justify-center text-zinc-600 hover:text-tiger"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <p className="font-heading text-sm font-bold text-tiger">{formatPrice(item.price * item.qty)}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-zinc-200 px-6 py-5">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-body text-sm text-zinc-500">Subtotal</span>
                  <span className="font-display text-xl text-zinc-900" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for your receipt (optional)"
                  data-testid="cart-email-input"
                  className="mb-3 w-full border border-zinc-300 bg-white px-4 py-3 font-body text-sm text-zinc-900 placeholder-zinc-400 outline-none transition-colors focus:border-tiger"
                />
                <button
                  data-testid="checkout-button"
                  disabled={checkingOut}
                  onClick={handleCheckout}
                  className="w-full bg-tiger py-4 font-heading text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-tiger-hover disabled:opacity-60"
                >
                  {checkingOut ? "Redirecting to Stripe..." : "Checkout Securely"}
                </button>
                <p className="mt-3 text-center font-body text-[10px] uppercase tracking-widest text-zinc-400">
                  Secure payment by Stripe · Fulfilled on demand via Printful
                </p>
                <p className="mt-1.5 text-center font-body text-[10px] uppercase tracking-widest text-tiger" data-testid="promo-code-hint">
                  Have a promo code? Apply it on the payment page
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
